import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Select from "react-select";
import DataTable from "react-data-table-component";
import * as yup from "yup";
import { useFormik } from "formik";
import jsPDF from "jspdf";

/* COMPONENTS */
import Input from "../../components/input";

/* MATERIAL TAILWIND COMPONENTS */
import {
  Button,
  Dialog,
  DialogBody,
  Tooltip,
  Card,
  CardBody,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";

/* ICONS */
import { IoClose } from "react-icons/io5";
import { FaDownload } from "react-icons/fa";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import {
  getAwardApplications,
  deleteAwardApplicationById,
} from "../../service/api/contactUs.api";

/* CONFIG */
import { IMAGE_URL } from "../../config";
import ExcelExport from "components/export";
import { GoKebabHorizontal } from "react-icons/go";
import {
  sendEmailToSelected,
  updateApplication,
  updateUserStatusJury,
  updateUserStatusOffice,
} from "service/api/auth.api";
import moment from "moment";
import "../../assets/IBMPlexSans-bold";
import "../../assets/IBMPlexSans-normal";

const exportTeacherPDF = async (application) => {
  const getBase64FromUrl = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const lineHeight = 8;
  const sectionPadding = 5;
  const sectionTopPadding = 5;
  let y = margin;

  const maxWidth = pageWidth - 2 * margin;
  const labelX = margin + sectionPadding;
  const valueX = margin + 70;

  // ✅ Page break checker
  const checkPageBreak = (requiredHeight = lineHeight) => {
    if (y + requiredHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // === HEADER IMAGE ===
  const logoBase64 = await getBase64FromUrl(
    "https://storage.googleapis.com/cdn-roundtechsquare/lohana-mahaparishad/form-header.png"
  );
  const tempImg = new Image();
  tempImg.src = logoBase64;

  await new Promise((resolve) => {
    tempImg.onload = resolve;
  });

  const imgRatio = tempImg.height / tempImg.width;
  const imgWidth = pageWidth;
  const imgHeight = imgWidth * imgRatio;

  checkPageBreak(imgHeight);
  doc.addImage(logoBase64, "PNG", 0, y, imgWidth, imgHeight);
  y += imgHeight;

  const addWrappedText = (text, x, yPos, maxW) => {
    const lines = doc.splitTextToSize(text || "N/A", maxW);
    doc.text(lines, x, yPos);
    return yPos + lines.length * lineHeight;
  };

  const addLine = (label, value) => {
    checkPageBreak(lineHeight * 2);

    // Wrap long values
    const wrappedValue = doc.splitTextToSize(
      value || "N/A",
      maxWidth - (valueX - margin)
    );
    const requiredHeight = wrappedValue.length * lineHeight;

    // Label
    doc.setFont("IBMPlexSans", "bold");
    doc.text(`${label}:`, labelX, y);

    // Value
    doc.setFont("IBMPlexSans", "normal");
    doc.text(wrappedValue, valueX, y);

    // Move Y correctly (prevent overlap)
    y += requiredHeight + 4; // extra padding
  };

  const addWrappedLink = (label, url, displayText = "View Document") => {
    if (!url) return;
    checkPageBreak(lineHeight * 2);
    doc.setFont("IBMPlexSans", "bold");
    doc.text(`${label}:`, labelX, y);
    doc.setFont("IBMPlexSans", "normal");
    const linkWidth = doc.getTextWidth(displayText);
    doc.setTextColor(0, 0, 255);
    doc.text(displayText, valueX, y);
    doc.link(valueX, y - lineHeight + 2, linkWidth, lineHeight, { url });
    doc.setTextColor(0, 0, 0);
    y += lineHeight + 4;
  };

  const getFileUrl = (path) => (path ? `${IMAGE_URL}${path}` : null);

  const fullName = `${application.firstName || ""} ${
    application.middleName || ""
  } ${application.lastName || ""}`.trim();

  const addSection = (title, drawContentCallback) => {
    checkPageBreak(lineHeight + 12);
    doc.setFontSize(13);
    doc.setFont("IBMPlexSans", "bold");
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, y, maxWidth, lineHeight + 6, "F");
    doc.text(title, labelX, y + lineHeight);
    y += lineHeight + 14;
    doc.setFontSize(11);
    doc.setFont("IBMPlexSans", "bold");
    drawContentCallback();
    y += 6;
  };

  addSection("Personal Information", () => {
    addLine("Full Name", fullName);
    addLine("Email", application.email);
    addLine("Mobile", application.mobileNumber);
    addLine("Date of Birth", application.dateOfBirth);
    addLine("Application Date", application.dateOfApplication);
    addLine("Lohana Connect ID", application.lohanaConnectId);
    addLine("Address", application.address);
    addLine("State", application.state);
    addLine("District", application.district);
    addLine("Zone", application.zone);
    addLine("Mahajan", application.mahajan);
    addLine(
      "Award Category",
      Array.isArray(application.awardCategory)
        ? application.awardCategory.join(", ")
        : application.awardCategory
    );
    addWrappedLink("Photograph", getFileUrl(application.photograph));
    addWrappedLink("Signature", getFileUrl(application.applicantSignature));
  });

  if (application.teachingProfessionals) {
    const tp = application.teachingProfessionals;
    addSection("Professional Details", () => {
      addLine("Institute", tp.institute);
      addLine("Experience", `${tp.experienceYears} years`);
      addLine(
        "Qualifications",
        [tp.qualification1, tp.qualification2, tp.qualification3]
          .filter(Boolean)
          .join(", ")
      );
      addLine("Summary", tp.summary);
      addLine("Achievements", tp.achievements);
      addWrappedLink("Profile", getFileUrl(tp.profile));
    });
  }

  if (application.documents) {
    const docs = application.documents;
    addSection("Supporting Documents", () => {
      addWrappedLink("Passport Photo", getFileUrl(docs.passportSizePhotograph));
      addWrappedLink("Proof of Identity", getFileUrl(docs.proofOfIdentity));
      addWrappedLink(
        "Letter of Recommendation",
        getFileUrl(docs.letterOfRecommendation)
      );
      addWrappedLink(
        "Registration Certificate",
        getFileUrl(docs.copyOfRegistrationCertificate)
      );
      addWrappedLink(
        "Achievement Certificates",
        getFileUrl(docs.certifiedTranscriptsAndAchievementCertificates)
      );
      addWrappedLink("Proof of Age", getFileUrl(docs.proofOfAge));
      addWrappedLink("Registration Proof", getFileUrl(docs.registrationProof));
      addWrappedLink(
        "Additional Documents",
        getFileUrl(docs.additionalDocuments)
      );
    });
  }

  doc.save(`${fullName}_Teacher_Application.pdf`);
};

const zones = [
  "Saurashtra 1",
  "Saurashtra 2",
  "Saurashtra 3",
  "Saurashtra 4",
  "Kutch",
  "North Gujarat",
  "Central Gujarat",
  "South Gujarat",
  "Mumbai",
  "Maharashtra",
  "Central India",
  "Vidarbha",
  "South India",
  "East India",
  "North India",
];

const statesOfIndia = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const officeBearerOptions = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
];

const officeBearerOptionsUpdated = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const juryOptions = [
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
];

const statusOptionsForSuperAdmin = [
  { value: null, label: "All status" },
  { value: "pending", label: "Pending" },
  { value: "eligible", label: "Eligible" },
  { value: "rejected", label: "Rejected" },
  { value: "ineligible", label: "Ineligible" },
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const statusOptionsForJury = [
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "selected", label: "Selected" },
  { value: "not-selected", label: "Not Selected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const statusOptionsForOfficeBearer = [
  { value: null, label: "All status" },
  { value: "pending", label: "Pending" },
  { value: "eligible", label: "Eligible" },
  { value: "ineligible", label: "Ineligible" },
  { value: "rejected", label: "Rejected" },
  { value: "shortlisted", label: "Shortlisted" },
];

const validationSchema = yup.object().shape({
  applicationStatus: yup.object().required("Status is required"),
});

const Teacher = () => {
  /* STATE VARIABLES */
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(100);

  /* LIST OF DATA */
  const [dataList, setDataList] = useState([]);

  /* TOTAL DOCS */
  const [totalDocs, setTotalDocs] = useState(0);

  /* SEARCH VALUE */
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  /* EXPORTED DATA */
  const [exportData, setExportData] = useState([]);

  /* DELETE ID */
  const [deleteId, setDeleteId] = useState(null);

  /* DELETE MODAL */
  const [deleteModal, setDeleteModal] = useState(false);

  /* SELECTED APPLICATION */
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);

  /* STATE OPTIONS */
  const [stateOptions, setStateOptions] = useState([]);

  /* ZONE OPTIONS */
  const [zoneOptions, setZoneOptions] = useState([]);

  /* SELECTED STATE AND ZONE */
  const [selectedState, setSelectedState] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);

  /* STATUS DIALOG */
  const [statusDialog, setStatusDialog] = useState(false);
  const [juryStatusDialog, setJuryStatusDialog] = useState(false);

  /* USER ROLE */
  const [userRole, setUserRole] = useState("");

  /* STATUS LOADING */
  const [statusLoading, setStatusLoading] = useState(false);

  /* SELECTED ROLE */
  const [selectedRole, setSelectedRole] = useState(null);

  /* SELECTED ID */
  const [selectedId, setSelectedId] = useState();

  /* REWARDS DIALOG */
  const [rewardsDialog, setRewardsDialog] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);

  /* DEFAULT OPTION */
  const [defaultSelected, setDefaultSelected] = useState(null);

  /* SEND EMAIL DIALOG */
  const [sendEmailDialog, setSendEmailDialog] = useState(false);

  /* EDIT MODAL DIALOG */
  const [editModal, setEditModal] = useState(false);
  const [isShortlistedValue, setIsShortlistedValue] = useState("");

  /* GET LIST OF DATA */
  const getDataListFunction = () => {
    setLoading(true);
    const params = {
      page: 1,
      sizePerPage: 10000,
      pagination: true,
    };

    if (selectedState !== null) {
      params.state = selectedState?.value;
    }

    if (selectedZone !== null) {
      params.zone = selectedZone?.value;
    }

    if (defaultSelected !== null && defaultSelected?.value !== "shortlisted") {
      params.applicationStatus = defaultSelected?.value;
    } else if (defaultSelected?.value === "shortlisted") {
      params.applicationStatus = "eligible";
      params.isShortListed = true;
    }

    getAwardApplications(params)
      .then((res) => {
        let allData = res?.data?.docs || [];
        allData = allData.filter((item) => {
          return item.teachingProfessionals;
        });
        if (search.trim()) {
          const searchLower = search.toLowerCase();

          allData = allData.filter((item) => {
            const teacherFullName = `${item.firstName || ""} ${
              item.middleName || ""
            } ${item.lastName || ""}`.toLowerCase();
            const teacherEmail = item.email ? item.email.toLowerCase() : "";

            return (
              teacherFullName.includes(searchLower) ||
              teacherEmail.includes(searchLower)
            );
          });
        }
        const startIndex = (page - 1) * sizePerPage;
        const endIndex = startIndex + sizePerPage;
        const paginatedData = allData.slice(startIndex, endIndex);
        setDataList(paginatedData);
        setTotalDocs(allData.length);
      })
      .catch((e) => {
        console.error("Error in component:", e);
        toast.error("Failed to fetch data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* EXCEL EXPORT */
  const getExportList = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      const params = {
        page: 1,
        sizePerPage: 10000,
        pagination: true,
      };

      if (selectedState !== null) {
        params.state = selectedState?.value;
      }

      if (selectedZone !== null) {
        params.zone = selectedZone?.value;
      }

      if (
        defaultSelected !== null &&
        defaultSelected?.value !== "shortlisted"
      ) {
        params.applicationStatus = defaultSelected?.value;
      } else if (defaultSelected?.value === "shortlisted") {
        params.applicationStatus = "eligible";
        params.isShortListed = true;
      }

      getAwardApplications(params)
        .then((res) => {
          let allData = res?.data?.docs || [];

          /* FILTER ONLY TEACHER DATA */
          allData = allData.filter((item) => {
            return item.teachingProfessionals;
          });

          const tempArray = allData?.map((item, index) => {
            return {
              "Sr. No:": index + 1,
              "Teacher Name": `${item.firstName || ""} ${
                item.middleName || ""
              } ${item.lastName || ""}`,
              State: item?.state ?? "N/A",
              District: item?.district ?? "N/A",
              Zone: item?.zone ?? "N/A",
              Mahajan: item?.mahajan ?? "N/A",
              Email: item?.email ?? "N/A",
              "Mobile Number": item?.mobileNumber ?? "N/A",
              "Application Date": item?.dateOfApplication ?? "N/A",
              "Lohana Connect ID": item?.lohanaConnectId ?? "N/A",
              "Date of birth": item?.dateOfBirth ?? "N/A",
              Address: item?.address ?? "N/A",
              Department: item?.affiliation?.department ?? "N/A",
              Institution: item?.affiliation?.institution ?? "N/A",
              Achievements: item?.teachingProfessionals?.achievements ?? "N/A",
              "Years of experience":
                item?.teachingProfessionals?.experienceYears ?? "N/A",
              Qualifications: `${item?.teachingProfessionals?.qualification1}, ${item?.teachingProfessionals?.qualification2}, ${item?.teachingProfessionals?.qualification3}`,
              Summary: item?.teachingProfessionals?.summary ?? "N/A",
              "Transcript and Achievement Certificate": `https://storage.googleapis.com/kidsstreet-test/${item?.documents?.certifiedTranscriptsAndAchievementCertificates}`,
              "Letter of Recommendation": `https://storage.googleapis.com/kidsstreet-test/${item?.documents?.letterOfRecommendation}`,
              "Proof of Identity": `https://storage.googleapis.com/kidsstreet-test/${item?.documents?.proofOfIdentity}`,
              Profile: `https://storage.googleapis.com/kidsstreet-test/${item?.teachingProfessionals?.profile}`,

              /* STATUS */
              "Application Status":
                defaultSelected?.value === "shortlisted"
                  ? "Eligible & Shortlisted"
                  : item?.applicationStatus,

              /* OFFICE BEARER REMARKS */
              "Office Bearer Remarks":
                (item.officeBearerRemarks || [])
                  .map(
                    (r) =>
                      `${r.remark} (by ${r.addedBy?.firstName || ""} ${
                        r.addedBy?.lastName || ""
                      } on ${moment(r.createdAt).format("DD MMM")})`
                  )
                  .join(", ") || "No remarks",

              /* JURY BEARER REMARKS */
              "Jury Remarks":
                (item.juryRemarks || [])
                  .map(
                    (r) =>
                      `${r.remark} (by ${r.addedBy?.firstName || ""} ${
                        r.addedBy?.lastName || ""
                      } on ${moment(r.createdAt).format("DD MMM")})`
                  )
                  .join(", ") || "No remarks",
            };
          });

          setExportData(tempArray);
          resolve(tempArray);
        })
        .catch((e) => {
          console.error("Export data fetch error:", e);
          toast.error("Failed to fetch export data");
          reject(e);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  /* FUNCTIONS */
  const handlePageChange = async (e) => {
    setPage(e);
    //getDataListFunction();
  };

  /* HANDLE PER PAGE */
  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
    //getDataListFunction();
  };

  /* HANDLE DELETE DATA */
  const handleDelete = () => {
    setLoading(true);
    deleteAwardApplicationById(deleteId)
      .then((res) => {
        toast.success(res?.message || "Application deleted successfully");
        setDataList((prev) => prev.filter((item) => item._id !== deleteId));
      })
      .catch((e) => {
        console.error("Error deleting application:", e);
        toast.error(
          e?.response?.data?.message || "Failed to delete application"
        );
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteId(null);
        setLoading(false);
      });
  };

  /* HANDLE SEND EMAIL */
  const handleSendEmail = () => {
    setLoading(true);
    sendEmailToSelected(deleteId)
      .then((res) => {
        toast.success(res?.message || "The email has been sent successfully");
      })
      .catch((e) => {
        console.error("Error deleting application:", e);
        toast.error(
          e?.response?.data?.message || "Failed to delete application"
        );
      })
      .finally(() => {
        setSendEmailDialog(false);
        setDeleteId(null);
        setLoading(false);
      });
  };

  /* GET APPLICANT'S FULL NAME */
  const getFullName = (application) => {
    const { firstName, middleName, lastName } = application;
    return `${firstName || ""} ${middleName || ""} ${lastName || ""}`.trim();
  };

  /* VIEW APPLICATION DETAILS */
  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setDetailsModal(true);
  };

  /* FORMAT DATE */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  /* GET FULL FILE URL */
  const getFullFileUrl = (path) => {
    if (!path) return null;
    return `${IMAGE_URL}${path}`;
  };

  /* UPDATE FORMIK */
  const updateFormik = useFormik({
    initialValues: {
      applicationStatus: "",
      remarks: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setStatusLoading(true);
      let payload = {};

      if (values?.applicationStatus?.value === "shortlisted") {
        payload = {
          isShortListed: true,
          applicationStatus: "eligible",
        };
      } else if (values?.remarks !== "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
          remarks: values?.remarks,
        };
      } else if (values?.remarks === "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
        };
      } else {
        console.log("Not received payload");
      }

      updateUserStatusOffice(selectedId, payload)
        .then((res) => {
          if (res?.status) {
            toast.success("The status has been updated successfully");
            setStatusDialog(!statusDialog);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          console.log("Error: ", e);
        })
        .finally(() => {
          setStatusLoading(!statusLoading);
          getDataListFunction();
          updateFormik.resetForm();
        });
    },
  });

  /* UPDATE FORMIK JURY */
  const updateFormikJury = useFormik({
    initialValues: {
      applicationStatus: "",
      remarks: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setStatusLoading(true);
      let payload = {};

      if (values?.remarks !== "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
          remarks: values?.remarks,
        };
      }

      if (values?.remarks === "") {
        payload = {
          applicationStatus: values?.applicationStatus?.value,
        };
      }

      updateUserStatusJury(selectedId, payload)
        .then((res) => {
          if (res?.status) {
            toast.success("The status has been updated successfully");
            setJuryStatusDialog(!juryStatusDialog);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          console.log("Error: ", e);
        })
        .finally(() => {
          setStatusLoading(!statusLoading);
          getDataListFunction();
          updateFormikJury.resetForm();
        });
    },
  });

  /* HANDLE STATUS DIALOG */
  const handleStatusDialogOpen = (row) => {
    setSelectedId(row?._id);
    const user = JSON.parse(localStorage.getItem("user"));
    setIsShortlistedValue(row?.applicationStatus);

    if (row?.applicationStatus && row?.applicationStatus !== "pending") {
      const currentStatusOption = officeBearerOptions.find(
        (option) => option.value === row.applicationStatus
      );
      updateFormik.setValues({
        applicationStatus: currentStatusOption || "",
        remarks:
          row?.officeBearerRemarks?.filter(
            (item) => item?.addedBy?._id === user?._id
          )?.remark || "",
      });
      setSelectedRole(currentStatusOption || null);
    } else {
      updateFormik.resetForm();
      setSelectedRole(null);
    }

    setStatusDialog(true);
  };

  /* HANDLE STATUS DIALOG */
  const handleStatusDialogOpenJury = (row) => {
    setSelectedId(row?._id);
    const user = JSON.parse(localStorage.getItem("user"));

    if (row?.applicationStatus && row?.applicationStatus !== "pending") {
      const currentStatusOption = juryOptions.find(
        (option) => option.value === row.applicationStatus
      );
      updateFormikJury.setValues({
        applicationStatus: currentStatusOption || "",
        remarks:
          row?.juryRemarks?.filter((item) => item?.addedBy?._id === user?._id)
            ?.remark || "",
      });
      setSelectedRole(currentStatusOption || null);
    } else {
      updateFormikJury.resetForm();
      setSelectedRole(null);
    }

    setJuryStatusDialog(true);
  };

  /* HANDLE STATUS DIALOG CLOSE */
  const handleStatusDialogClose = () => {
    setStatusDialog(false);
    updateFormik.resetForm();
    setSelectedRole(null);
    setSelectedId(null);
  };

  /* HANDLE STATUS DIALOG CLOSE */
  const handleStatusDialogCloseJury = () => {
    setJuryStatusDialog(false);
    updateFormikJury.resetForm();
    setSelectedRole(null);
    setSelectedId(null);
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.5,
      selector: (row, index) => (
        <span className="text-sm font-ibm-regular text-black text-opacity-60">
          {index + 1}
        </span>
      ),
    },
    {
      name: "Applicant Name",
      grow: 1.2,
      selector: (row) => (
        <div className="flex flex-col py-2">
          <p className="text-sm font-ibm-semibold text-black">
            {getFullName(row)}
          </p>
        </div>
      ),
    },
    {
      name: "Email & Mobile",
      grow: 1.5,
      selector: (row) => {
        return (
          <div className="flex flex-col gap-1 mt-1">
            <a
              href={`mailto:${row.email}`}
              className="text-sm text-blue-500 hover:underline"
            >
              {row.email}
            </a>
            <a
              href={`tel:${row.mobileNumber}`}
              className="text-sm text-blue-500 hover:underline"
            >
              {row.mobileNumber}
            </a>
          </div>
        );
      },
    },
    {
      name: "Status",
      grow: 0.8,
      selector: (row) => {
        return (
          <div className="p-0">
            {row?.applicationStatus === "eligible" ? (
              <div className="w-fit bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Eligible
              </div>
            ) : row?.applicationStatus === "ineligible" ? (
              <div className="w-fit bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Ineligible
              </div>
            ) : row?.applicationStatus === "rejected" ? (
              <div className="w-fit bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-1 rounded-md">
                Rejected
              </div>
            ) : row?.applicationStatus === "pending" ? (
              <div className="w-fit bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-1 rounded-md">
                Pending
              </div>
            ) : row?.applicationStatus === "selected" ? (
              <div className="w-fit bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-1 rounded-md">
                Selected
              </div>
            ) : row?.applicationStatus === "not-selected" ? (
              <div className="w-fit bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-1 rounded-md">
                Not selected
              </div>
            ) : (
              <></>
            )}
            {row?.isShortListed && (
              <div className="mt-2 bg-logoRed/10 border border-logoRed text-logoRed text-sm font-ibm-regular px-3 py-1 rounded-md">
                Shortlisted
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: "Date Applied",
      grow: 0.8,
      selector: (row) => (
        <div>
          <p className="text-sm font-ibm-regular">
            {formatDate(row.dateOfApplication)}
          </p>
        </div>
      ),
    },
    {
      name: "Actions",
      grow: 0.5,
      selector: (row) => (
        <Menu>
          <MenuHandler>
            <Button className="shadow-none hover:shadow-none p-1.5 bg-greyBg border border-greyBorder hover:border-black/40 rounded-full transition-all duration-300 ease-in-out">
              <GoKebabHorizontal className="w-4 h-4 text-greyText rotate-90" />
            </Button>
          </MenuHandler>
          <MenuList className="p-1.5 min-w-[20vh] bg-white">
            {/* UPDATE STATUS - OFFICE BEARER */}
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => handleStatusDialogOpen(row)}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Update status
              </MenuItem>
            )}

            {/* UPDATE STATUS - JURY */}
            {userRole === "JURY" && (
              <MenuItem
                onClick={() => handleStatusDialogOpenJury(row)}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Update status
              </MenuItem>
            )}

            {/* EDIT APPLICATION */}
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => {
                  setSelectedApplication(row);

                  setEditModal(true);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Edit application
              </MenuItem>
            )}

            {/* VIEW REMARKS */}
            {row?.officeBearerRemarks?.length > 0 && (
              <MenuItem
                onClick={() => {
                  setRewardsDialog(!rewardsDialog);
                  setSelectedUserDetails(row);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                View remarks
              </MenuItem>
            )}

            {/* VIEW DETAILS */}
            <MenuItem
              onClick={() => viewApplicationDetails(row)}
              className="text-sm font-ibm-regular px-4 py-2 text-black"
            >
              View details
            </MenuItem>

            {/* EXPORT TO PDF */}
            <MenuItem
              onClick={() => {
                setSelectedApplication(row);
                setDetailsModal(true);
                setTimeout(() => {
                  exportTeacherPDF(row);
                }, 500);
              }}
              className="text-sm font-ibm-regular px-4 py-2 text-black"
            >
              Export to PDF
            </MenuItem>

            {/* SEND EMAIL - OFFICE BEARER */}
            {userRole === "OFFICE-BEARER" &&
            row?.applicationStatus === "selected" ? (
              <MenuItem
                onClick={() => {
                  setSendEmailDialog(!sendEmailDialog);
                  setDeleteId(row?._id);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-black"
              >
                Send email to teacher
              </MenuItem>
            ) : (
              <></>
            )}

            {/* DELETE APPLICATION - OFFICE BEARER */}
            {userRole === "OFFICE-BEARER" && (
              <MenuItem
                onClick={() => {
                  setDeleteId(row?._id);
                  setDeleteModal(!deleteModal);
                }}
                className="text-sm font-ibm-regular px-4 py-2 text-red-400"
              >
                Delete application
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    if (userRole !== "" && defaultSelected !== null) {
      getDataListFunction();
    }
  }, [
    page,
    sizePerPage,
    debouncedSearch,
    selectedState,
    selectedZone,
    defaultSelected,
  ]);

  /* USE-EFFECTS */
  useEffect(() => {
    const formattedOptions = [
      { value: null, label: "All states" },
      ...statesOfIndia.map((state) => ({
        value: state,
        label: state,
      })),
    ];

    setStateOptions(formattedOptions);
  }, []);

  /* USE-EFFECTS */
  useEffect(() => {
    const formattedOptions = [
      { value: null, label: "All zones" },
      ...zones.map((zone) => ({
        value: zone,
        label: zone,
      })),
    ];

    setZoneOptions(formattedOptions);
  }, []);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-regular text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-regular text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  /* NO DATA SCREEN */
  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-regular text-black text-center">
          There are no teachers to display.
        </p>
      </div>
    );
  };

  /* RENDER DETAIL FIELD */
  const renderDetailField = (label, value, isLink = false) => {
    const isImage =
      isLink &&
      value &&
      /\.(jpg|jpeg|png|gif|webp)$/i.test(value.toLowerCase());

    const getFilenameFromPath = (path) => {
      if (!path) return "";
      const parts = path.split("/");
      return parts[parts.length - 1];
    };

    return (
      <div className="mb-5 group">
        <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
        {isLink && value ? (
          isImage && label !== "Signature" ? (
            <div className="w-fit mb-2 relative">
              <img
                src={getFullFileUrl(value)}
                alt={label}
                className="max-h-40 object-contain border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 aspect-square bg-white"
              />
              <div className="absolute top-1 right-1">
                <div className="flex items-center gap-1">
                  <a
                    href={getFullFileUrl(value)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-red-600 hover:text-red-800 mt-1 border border-red-100 px-2 py-1 rounded-md bg-red-50 flex items-center gap-2 font-ibm-regular"
                  >
                    <FaDownload className="w-3 h-3 text-red-700" /> View
                  </a>
                </div>
              </div>
            </div>
          ) : isImage && label === "Signature" ? (
            <div className="w-fit mb-2 relative">
              <img
                src={getFullFileUrl(value)}
                alt="Signature"
                className="max-h-40 w-[30vh] object-contain rounded-md bg-white"
                style={{ pointerEvents: "none" }}
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href={getFullFileUrl(value)}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-red-600 hover:text-red-800 hover:underline flex items-center gap-1 transition-colors duration-200 bg-red-50 px-4 py-2 rounded-md border border-red-100 font-ibm-regular"
              >
                <FaDownload size={12} className="text-red-700" /> View Document
              </a>
            </div>
          )
        ) : (
          <p className="text-sm bg-greyBg border border-greyBorder text-black px-3 py-2 rounded-md transition-all duration-200 font-ibm-regular">
            {value || "N/A"}
          </p>
        )}
      </div>
    );
  };

  /* RENDER EDITABLE FIELDS */
  const renderEditField = (label, name, type = "text") => (
    <div className="mb-0">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <Input
        name={name}
        type={type}
        value={editFormik.values[name]}
        onChange={editFormik.handleChange}
        className="w-full"
      />
    </div>
  );

  /* RENDER TEXTAREA */
  const renderTextareaField = (label, name, rows = "3") => (
    <div className="mb-0">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <textarea
        rows={rows}
        name={name}
        value={editFormik.values[name]}
        onChange={editFormik.handleChange}
        className="w-full block px-3 py-2 rounded-md border border-authBorder text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
      />
    </div>
  );

  /* RENDER FILE FIELDS */
  const renderFileField = (label, name) => (
    <div className="mb-5">
      <p className="text-sm font-ibm-regular text-greyText mb-1">{label}</p>
      <input
        type="file"
        name={name}
        onChange={(e) => editFormik.setFieldValue(name, e.target.files[0])}
        className="w-full font-ibm-regular"
      />
      {editFormik.values[name] &&
        typeof editFormik.values[name] === "string" && (
          <a
            href={getFullFileUrl(editFormik.values[name])}
            target="_blank"
            rel="noreferrer"
            className="mt-2 text-sm text-blue-500 hover:underline block font-ibm-regular underline"
          >
            View current uploaded file
          </a>
        )}
    </div>
  );

  /* EDIT FORMIK */
  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      /* PERSONAL INFORMATION */
      firstName: selectedApplication?.firstName,
      middleName: selectedApplication?.middleName,
      lastName: selectedApplication?.lastName,
      state: selectedApplication?.state,
      district: selectedApplication?.district,
      zone: selectedApplication?.zone,
      mahajan: selectedApplication?.mahajan,
      email: selectedApplication?.email,
      applicationDate: selectedApplication?.dateOfApplication,
      mobileNumber: selectedApplication?.mobileNumber,
      lohanaConnectId: selectedApplication?.lohanaConnectId,
      dateOfBirth: moment(selectedApplication?.dateOfBirth).format(
        "YYYY-MM-DD"
      ),
      awardCategory: selectedApplication?.awardCategory[0],
      address: selectedApplication?.address,
      photograph: selectedApplication?.photograph,
      applicantSignature: selectedApplication?.applicantSignature,

      /* PROFESSIONAL DETAILS */
      achievements: selectedApplication?.teachingProfessionals?.achievements,
      experienceYears:
        selectedApplication?.teachingProfessionals?.experienceYears,
      institute: selectedApplication?.teachingProfessionals?.institute,
      profile: selectedApplication?.teachingProfessionals?.profile,
      qualification1:
        selectedApplication?.teachingProfessionals?.qualification1,
      qualification2:
        selectedApplication?.teachingProfessionals?.qualification2,
      qualification3:
        selectedApplication?.teachingProfessionals?.qualification3,
      summary: selectedApplication?.teachingProfessionals?.summary,

      /* AFFILIATION DETAILS */
      institution: selectedApplication?.affiliation?.institution,
      department: selectedApplication?.affiliation?.department,

      /* SUPPORTING DOCUMENTS */
      proofOfIdentity: selectedApplication?.documents?.proofOfIdentity,
      certifiedTranscriptsAndAchievementCertificates:
        selectedApplication?.documents
          ?.certifiedTranscriptsAndAchievementCertificates,
    },
    onSubmit: (values) => {
      setFormLoading(true);
      const formData = new FormData();

      /* PERSONAL INFORMATION */
      formData.append("firstName", values.firstName);
      formData.append("middleName", values.middleName);
      formData.append("lastName", values.lastName);
      formData.append("state", values.state);
      formData.append("district", values.district);
      formData.append("zone", values.zone);
      formData.append("mahajan", values.mahajan);
      formData.append("email", values.email);
      formData.append("mobileNumber", values.mobileNumber);
      formData.append("lohanaConnectId", values.lohanaConnectId);
      formData.append("dateOfBirth", values.dateOfBirth);
      formData.append("awardCategory", values.awardCategory);
      formData.append("address", values.address);
      formData.append("photograph", values.photograph);
      formData.append("applicantSignature", values.applicantSignature);

      /* PROFESSIONAL DETAILS */
      formData.append(
        "teachingProfessionals.achievements",
        values.achievements
      );
      formData.append(
        "teachingProfessionals.experienceYears",
        values.experienceYears
      );
      formData.append("teachingProfessionals.institute", values.institute);
      formData.append("teachingProfessionals.profile", values.profile);
      formData.append(
        "teachingProfessionals.qualification1",
        values.qualification1
      );
      formData.append(
        "teachingProfessionals.qualification2",
        values.qualification2
      );
      formData.append(
        "teachingProfessionals.qualification3",
        values.qualification3
      );
      formData.append("teachingProfessionals.summary", values.summary);

      /* AFFILIATION DETAILS */
      formData.append("affiliation.institution", values.institution);
      formData.append("affiliation.department", values.department);

      /* SUPPORTING DOCUMENTS */
      formData.append("documents.proofOfIdentity", values.proofOfIdentity);
      formData.append(
        "documents.certifiedTranscriptsAndAchievementCertificates",
        values.certifiedTranscriptsAndAchievementCertificates
      );

      updateApplication(selectedApplication?._id, formData)
        .then((res) => {
          if (res?.status) {
            toast.success("The application has been updated successfully");
            setEditModal(false);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          console.log("Error: ", e);
        })
        .finally(() => {
          setFormLoading(false);
          getDataListFunction();
        });
    },
  });

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const userData = JSON.parse(user);
      setUserRole(userData.designation);
      if (userData.designation === "OFFICE-BEARER") {
        setDefaultSelected({ value: null, label: "All status" });
      } else {
        setDefaultSelected({ value: "eligible", label: "Eligible" });
      }
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  return (
    <section className="min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Teacher Applications | Admin Panel</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Teacher Applications
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage and view Teacher applications
            </h2>
          </div>
          <div className="w-full md:w-auto mt-2 md:mt-0 flex items-center gap-2">
            {/* EXCEL EXPORT BUTTON */}
            <ExcelExport
              data={exportData}
              fileName="Teacher Applications"
              btnText="Export data"
              onClickFn={async () => {
                try {
                  return await getExportList();
                } catch (error) {
                  console.error("Failed to prepare export data:", error);
                  throw error;
                }
              }}
            />

            {/* SEARCHBAR */}
            <div className="w-full md:w-[250px]">
              <Input
                type="text"
                placeholder="Search by teacher name, email."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full"
              />
            </div>

            {(selectedZone !== null || selectedState !== null) && (
              <button
                onClick={() => {
                  setSelectedZone(null);
                  setSelectedState(null);
                }}
                className="py-3 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 font-ibm-regular text-sm"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* FILTERS */}
        <div className="mt-5 flex items-center gap-3 justify-end">
          {/* FILTER BY STATUS */}
          {userRole === "SUPER-ADMIN" ? (
            <div className="w-full md:w-[200px]">
              <Select
                options={statusOptionsForSuperAdmin}
                className="react-select"
                classNamePrefix="dropdown"
                value={defaultSelected}
                onChange={(e) => setDefaultSelected(e)}
                placeholder="Filter by status"
              />
            </div>
          ) : (
            <div className="w-full md:w-[200px]">
              <Select
                options={
                  userRole === "OFFICE-BEARER"
                    ? statusOptionsForOfficeBearer
                    : statusOptionsForJury
                }
                className="react-select"
                classNamePrefix="dropdown"
                value={defaultSelected}
                onChange={(e) => setDefaultSelected(e)}
                placeholder="Filter by state"
              />
            </div>
          )}

          {/* FILTER BY ZONE */}
          <div className="w-full md:w-[200px]">
            <Select
              options={zoneOptions}
              className="react-select"
              classNamePrefix="dropdown"
              value={selectedZone}
              onChange={(e) => setSelectedZone(e)}
              placeholder={`${
                selectedZone === null ? "Select zone" : "Filter by zone"
              }`}
            />
          </div>

          {/* FILTER BY STATE */}
          <div className="w-full md:w-[200px]">
            <Select
              options={stateOptions}
              className="react-select"
              classNamePrefix="dropdown"
              value={selectedState}
              onChange={(e) => setSelectedState(e)}
              placeholder="Filter by state"
            />
          </div>
        </div>

        {/* DATA TABLE */}
        <div className="mt-5">
          <Card className="bg-white border border-greyBorder rounded-lg">
            <CardBody className="p-5">
              <DataTable
                columns={columns}
                data={dataList.filter((item) => item?.firstName !== "Testing")}
                progressPending={loading}
                progressComponent={<LoadingScreen />}
                noDataComponent={<NoDataScreen />}
                pagination
                paginationServer
                paginationTotalRows={totalDocs}
                onChangeRowsPerPage={handlePerPageChange}
                onChangePage={handlePageChange}
                className="data-table"
              />
            </CardBody>
          </Card>
        </div>
      </div>

      {/* DELETE MODAL */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Delete Application
              </h5>
              <Button
                onClick={() => setDeleteModal(!deleteModal)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <div className="p-5">
            <p className="text-sm font-ibm-regular text-black">
              Are you sure you want to delete this teacher application? This
              action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={() => setDeleteModal(!deleteModal)}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 text-sm font-ibm-regular text-black"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {/* APPLICATION DETAILS MODAL */}
      <Dialog
        open={detailsModal}
        handler={() => setDetailsModal(!detailsModal)}
        size="lg"
        className="bg-white rounded-xl shadow-xl"
      >
        <DialogBody className="p-0">
          {/* {console.log("Selected Application Data:", selectedApplication)} */}
          {selectedApplication && (
            <>
              <div className="flex items-center justify-between px-5 py-3 border-b border-greyBorder rounded-t-xl">
                <h5 className="text-lg font-ibm-bold text-black">
                  Teacher Application Details
                </h5>
                <Button
                  onClick={() => setDetailsModal(!detailsModal)}
                  className="p-2 rounded-full shadow-none hover:shadow-none bg-white hover:bg-red-50"
                >
                  <IoClose className="w-5 h-5 text-red-800" />
                </Button>
              </div>

              <div className="mt-0 max-h-[70vh] overflow-y-auto p-5">
                {/* PERSONAL INFORMATION */}
                <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                  <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                    Personal Information
                  </h3>

                  {/* NAME */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="col-span-1">
                      {selectedApplication?.firstName &&
                        renderDetailField(
                          "First Name",
                          selectedApplication?.firstName
                        )}
                    </div>
                    <div className="col-span-1">
                      {selectedApplication.middleName &&
                        renderDetailField(
                          "Middle Name",
                          selectedApplication?.middleName
                        )}
                    </div>
                    <div className="col-span-1">
                      {selectedApplication.lastName &&
                        renderDetailField(
                          "Last Name",
                          selectedApplication?.lastName
                        )}
                    </div>
                  </div>

                  {/* STATE, ZONE AND MAHAJAN */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                    <div className="col-span-1">
                      {selectedApplication.state &&
                        renderDetailField("State", selectedApplication?.state)}
                    </div>
                    <div className="col-span-1">
                      {selectedApplication.district &&
                        renderDetailField(
                          "District",
                          selectedApplication?.district
                        )}
                    </div>
                    <div className="col-span-1">
                      {selectedApplication.zone &&
                        renderDetailField("Zone", selectedApplication?.zone)}
                    </div>
                    <div className="col-span-1">
                      {selectedApplication.mahajan &&
                        renderDetailField(
                          "Mahajan",
                          selectedApplication?.mahajan
                        )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {selectedApplication.email && (
                      <div>
                        {selectedApplication.email &&
                          renderDetailField("Email", selectedApplication.email)}
                        {selectedApplication.mobileNumber &&
                          renderDetailField(
                            "Mobile",
                            selectedApplication.mobileNumber
                          )}
                        {selectedApplication.dateOfBirth &&
                          renderDetailField(
                            "Date of Birth",
                            formatDate(selectedApplication.dateOfBirth)
                          )}
                        {selectedApplication.address &&
                          renderDetailField(
                            "Address",
                            selectedApplication.address
                          )}
                        {selectedApplication?.state &&
                          renderDetailField(
                            "State",
                            selectedApplication?.state
                          )}
                        {selectedApplication?.district &&
                          renderDetailField(
                            "District",
                            selectedApplication?.district
                          )}
                        {selectedApplication?.zone &&
                          renderDetailField(
                            "Zone",
                            selectedApplication?.zone || "N/A"
                          )}
                      </div>
                    )}
                    <div>
                      {selectedApplication.dateOfApplication &&
                        renderDetailField(
                          "Application Date",
                          formatDate(selectedApplication.dateOfApplication)
                        )}
                      {selectedApplication.lohanaConnectId &&
                        renderDetailField(
                          "Lohana Connect ID",
                          selectedApplication.lohanaConnectId
                        )}
                      {selectedApplication.awardCategory &&
                        renderDetailField(
                          "Award Category",
                          Array.isArray(selectedApplication.awardCategory)
                            ? selectedApplication.awardCategory.join(", ")
                            : "N/A"
                        )}
                      {selectedApplication.photograph &&
                        renderDetailField(
                          "Photograph",
                          selectedApplication.photograph,
                          true
                        )}
                      {selectedApplication.applicantSignature &&
                        renderDetailField(
                          "Signature",
                          selectedApplication.applicantSignature,
                          true
                        )}
                    </div>
                  </div>
                </div>

                {/* PROFESSIONAL DETAILS */}
                {selectedApplication.teachingProfessionals && (
                  <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                    <h3 className="text-md font-ibm-bold mb-4 text-red-800 border-b border-red-100 pb-2">
                      Professional Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      {/* TEACHING PROFESSIONALS */}
                      {selectedApplication.teachingProfessionals && (
                        <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                          <h4 className="font-ibm-bold text-sm mb-3 text-red-700">
                            Teaching Professional
                          </h4>
                          {renderDetailField(
                            "Institute",
                            selectedApplication.teachingProfessionals.institute
                          )}
                          {renderDetailField(
                            "Experience",
                            `${selectedApplication.teachingProfessionals.experienceYears} years`
                          )}
                          {renderDetailField(
                            "Qualifications",
                            [
                              selectedApplication.teachingProfessionals
                                .qualification1,
                              selectedApplication.teachingProfessionals
                                .qualification2,
                              selectedApplication.teachingProfessionals
                                .qualification3,
                            ]
                              .filter(Boolean)
                              .join(", ")
                          )}
                          {renderDetailField(
                            "Summary",
                            selectedApplication.teachingProfessionals.summary
                          )}
                          {renderDetailField(
                            "Achievements",
                            selectedApplication.teachingProfessionals
                              .achievements
                          )}
                          {renderDetailField(
                            "Profile",
                            selectedApplication.teachingProfessionals.profile,
                            true
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* AFFILIATION DETAILS */}
                {selectedApplication.affiliation && (
                  <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                    <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                      Professional/Academic Affiliation Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {selectedApplication.affiliation
                        ? renderDetailField(
                            "Institution",
                            selectedApplication.affiliation.institution
                          )
                        : null}
                      {selectedApplication.affiliation
                        ? renderDetailField(
                            "Department",
                            selectedApplication.affiliation.department
                          )
                        : null}
                      {selectedApplication?.affiliation
                        ? renderDetailField(
                            "University",
                            selectedApplication.affiliation.university
                          )
                        : null}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {selectedApplication.documents && (
                  <div className="border border-greyBorder rounded-lg p-5 bg-white shadow">
                    <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                      Supporting Documents
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
                      {selectedApplication.documents &&
                      selectedApplication.documents.passportSizePhotograph
                        ? renderDetailField(
                            "Passport Size Photograph",
                            selectedApplication.documents
                              .passportSizePhotograph,
                            true
                          )
                        : null}
                      {selectedApplication.documents &&
                      selectedApplication.documents.proofOfIdentity
                        ? renderDetailField(
                            "Proof of Identity",
                            selectedApplication.documents.proofOfIdentity,
                            true
                          )
                        : null}
                      {selectedApplication.documents &&
                      selectedApplication.documents.letterOfRecommendation
                        ? renderDetailField(
                            "Letter of Recommendation",
                            selectedApplication.documents
                              .letterOfRecommendation,
                            true
                          )
                        : null}
                      {selectedApplication.documents &&
                      selectedApplication.documents
                        .copyOfRegistrationCertificate
                        ? renderDetailField(
                            "Copy of Registration Certificate",
                            selectedApplication.documents
                              .copyOfRegistrationCertificate,
                            true
                          )
                        : null}
                      {selectedApplication.documents &&
                      selectedApplication.documents
                        .certifiedTranscriptsAndAchievementCertificates
                        ? renderDetailField(
                            "Certified Transcripts and Achievement Certificates",
                            selectedApplication.documents
                              .certifiedTranscriptsAndAchievementCertificates,
                            true
                          )
                        : null}
                      {selectedApplication.documents &&
                      selectedApplication.documents.proofOfAge
                        ? renderDetailField(
                            "Proof of Age",
                            selectedApplication.documents.proofOfAge,
                            true
                          )
                        : null}
                      {selectedApplication.documents &&
                      selectedApplication.documents.registrationProof
                        ? renderDetailField(
                            "Registration Proof",
                            selectedApplication.documents.registrationProof,
                            true
                          )
                        : null}
                      {selectedApplication.documents &&
                      selectedApplication.documents.additionalDocuments
                        ? renderDetailField(
                            "Additional Documents",
                            selectedApplication.documents.additionalDocuments,
                            true
                          )
                        : null}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogBody>
      </Dialog>

      {/* STATUS DIALOG - OFFICE BEARER */}
      <Dialog open={statusDialog} handler={handleStatusDialogClose} size="xs">
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder flex items-center justify-between">
            <h5 className="text-base font-ibm-semibold text-black">
              Update status
            </h5>
            <Button
              onClick={handleStatusDialogClose}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>

          {/* BODY */}
          <form onSubmit={updateFormik.handleSubmit} className="p-5">
            {/* STATUS */}
            <div className="p-0">
              <p className="text-sm font-ibm-regular text-greyText">
                Please select the status of the application
              </p>
              <div className="mt-1">
                <Select
                  options={
                    isShortlistedValue === "eligible"
                      ? officeBearerOptionsUpdated
                      : officeBearerOptions
                  }
                  className="react-select"
                  value={updateFormik.values.applicationStatus}
                  onChange={(e) => {
                    updateFormik.setFieldValue("applicationStatus", e);
                    setSelectedRole(e);
                  }}
                  classNamePrefix="dropdown"
                  placeholder="Select role"
                />
              </div>
            </div>

            {/* REMARKS */}
            {selectedRole !== null && selectedRole?.label !== "Shortlisted" && (
              <div className="mt-5">
                <label className="text-sm font-ibm-regular text-greyText">
                  {selectedRole?.label === "Eligible" ||
                  selectedRole?.label === "Ineligible"
                    ? "Any remarks"
                    : "Please enter the reason of rejection"}
                </label>
                <div className="mt-1">
                  <textarea
                    rows={3}
                    placeholder={
                      selectedRole?.label === "Eligible" ||
                      selectedRole?.label === "Ineligible"
                        ? "Please enter the remarks"
                        : "Enter the reason of rejection"
                    }
                    name="remarks"
                    value={updateFormik.values.remarks}
                    onChange={updateFormik.handleChange}
                    className="block w-full px-5 py-3 rounded-md border border-authBorder  text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                type="submit"
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Update
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* STATUS DIALOG - JURY */}
      <Dialog
        open={juryStatusDialog}
        handler={handleStatusDialogCloseJury}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder flex items-center justify-between">
            <h5 className="text-base font-ibm-semibold text-black">
              Update status
            </h5>
            <Button
              onClick={handleStatusDialogCloseJury}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>

          {/* BODY */}
          <form onSubmit={updateFormikJury.handleSubmit} className="p-5">
            {/* STATUS */}
            <div className="p-0">
              <p className="text-sm font-ibm-regular text-greyText">
                Please select the status of the application
              </p>
              <div className="mt-1">
                <Select
                  options={juryOptions}
                  className="react-select"
                  value={updateFormikJury.values.applicationStatus}
                  onChange={(e) => {
                    updateFormikJury.setFieldValue("applicationStatus", e);
                    setSelectedRole(e);
                  }}
                  classNamePrefix="dropdown"
                  placeholder="Select status"
                />
              </div>
            </div>

            {/* REMARKS */}
            {selectedRole !== null && (
              <div className="mt-5">
                <label className="text-sm font-ibm-regular text-greyText">
                  Remarks (if any)
                </label>
                <div className="mt-1">
                  <textarea
                    rows={3}
                    placeholder="Please enter the remarks"
                    name="remarks"
                    value={updateFormikJury.values.remarks}
                    onChange={updateFormikJury.handleChange}
                    className="block w-full px-5 py-3 rounded-md border border-authBorder  text-sm font-ibm-regular text-black placeholder:text-sm placeholder:text-gray-500 placeholder:font-ibm-regular focus:outline-none"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                type="submit"
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Update
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* REMARKS DIALOG */}
      <Dialog
        open={rewardsDialog}
        handler={() => setRewardsDialog(!rewardsDialog)}
        size="md"
      >
        <DialogBody className="p-5 relative max-h-[80vh] overflow-auto hide-scrollbar">
          {/* OFFICE BEARER REMARKS */}
          <div className="pb-0">
            <h5 className="text-md font-ibm-semibold text-black">
              Office Bearer Remarks
            </h5>
            <div className="mt-2">
              {selectedUserDetails?.officeBearerRemarks?.map((item, index) => (
                <div
                  key={index}
                  className="mt-5 first:mt-0 bg-white border border-greyBorder rounded-lg shadow p-3"
                >
                  <div className="flex items-center justify-between pb-2 border-b border-greyBorder">
                    <div className="p-0">
                      <p className="text-black font-ibm-semibold text-base">
                        {item?.addedBy?.firstName} {item?.addedBy?.lastName}
                      </p>
                      <p className="text-sm font-ibm-regular text-greyText">
                        {moment(item?.createdAt).format("DD MMMM, HH:mm A")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item?.applicationStatus === "eligible" ? (
                        <div className="bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Eligible
                        </div>
                      ) : item?.applicationStatus === "ineligible" ? (
                        <div className="bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Ineligible
                        </div>
                      ) : item?.applicationStatus === "rejected" ? (
                        <div className="bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Rejected
                        </div>
                      ) : item?.applicationStatus === "pending" ? (
                        <div className="bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Pending
                        </div>
                      ) : item?.applicationStatus === "selected" ? (
                        <div className="bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Selected
                        </div>
                      ) : item?.applicationStatus === "not-selected" ? (
                        <div className="bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                          Not selected
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>

                  <div className="pt-3">
                    <p className="text-black font-ibm-regular">
                      {item?.remark}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* JURY REMARKS */}
          {selectedUserDetails?.juryRemarks?.length > 0 && (
            <div className="pt-5">
              <h5 className="text-md font-ibm-semibold text-black">
                Jury Remarks
              </h5>
              <div className="mt-2">
                {selectedUserDetails?.juryRemarks?.map((item, index) => (
                  <div
                    key={index}
                    className="mt-5 first:mt-0 bg-white border border-greyBorder rounded-lg shadow p-3"
                  >
                    <div className="flex items-center justify-between pb-2 border-b border-greyBorder">
                      <div className="p-0">
                        <p className="text-black font-ibm-semibold text-base">
                          {item?.addedBy?.firstName} {item?.addedBy?.lastName}
                        </p>
                        <p className="text-sm font-ibm-regular text-greyText">
                          {moment(item?.createdAt).format("DD MMMM, HH:mm A")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {item?.applicationStatus === "eligible" ? (
                          <div className="bg-green-500/10 border border-green-500/50 text-green-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Eligible
                          </div>
                        ) : item?.applicationStatus === "ineligible" ? (
                          <div className="bg-orange-500/10 border border-orange-500/50 text-orange-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Ineligible
                          </div>
                        ) : item?.applicationStatus === "rejected" ? (
                          <div className="bg-red-500/10 border border-red-500/50 text-red-600 text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Rejected
                          </div>
                        ) : item?.applicationStatus === "pending" ? (
                          <div className="bg-greyBg/50 border border-greyBorder text-greyText text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Pending
                          </div>
                        ) : item?.applicationStatus === "selected" ? (
                          <div className="bg-green-500 border border-green-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Selected
                          </div>
                        ) : item?.applicationStatus === "not-selected" ? (
                          <div className="bg-red-500 border border-red-500 text-white text-sm font-ibm-regular px-3 py-0.5 rounded-full">
                            Not selected
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>

                    <div className="pt-3">
                      <p className="text-black font-ibm-regular">
                        {item?.remark}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="absolute top-3 right-3">
            <Button
              onClick={() => setRewardsDialog(!rewardsDialog)}
              className="shadow-none hover:shadow-none bg-white hover:bg-black/10 rounded-full p-1 transition-all duration-300 ease-in-out"
            >
              <IoClose className="w-5 h-5 text-greyText" />
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* DELETE MODAL */}
      <Dialog
        open={sendEmailDialog}
        handler={() => setSendEmailDialog(!sendEmailDialog)}
        size="xs"
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="px-5 py-3 border-b border-greyBorder">
            <div className="flex items-center justify-between">
              <h5 className="text-base font-ibm-semibold text-black">
                Confirmation
              </h5>
              <Button
                onClick={() => setSendEmailDialog(!sendEmailDialog)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
              >
                <IoClose className="w-5 h-5 text-black" />
              </Button>
            </div>
          </div>

          {/* BODY */}
          <div className="p-5">
            <p className="text-sm font-ibm-regular text-black">
              Are you sure you want to send email to this applicant? This action
              cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={() => setSendEmailDialog(!sendEmailDialog)}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 text-sm font-ibm-regular text-black"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                className="py-2 px-4 rounded-md shadow-none hover:shadow-none bg-red-500 hover:bg-red-600 text-sm font-ibm-regular text-white"
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog
        open={editModal}
        handler={() => setEditModal(!editModal)}
        size="lg"
        className="bg-white rounded-xl shadow-xl"
      >
        <DialogBody className="p-0">
          <form onSubmit={editFormik.handleSubmit} className="p-0">
            {/* HEADING */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-greyBorder rounded-t-xl">
              <h5 className="text-lg font-ibm-bold text-black">
                Teacher Application Details
              </h5>
              <Button
                onClick={() => setEditModal(!editModal)}
                className="p-2 rounded-full shadow-none hover:shadow-none bg-white hover:bg-red-50"
              >
                <IoClose className="w-5 h-5 text-red-800" />
              </Button>
            </div>

            {/* BODY */}
            <div className="mt-0 max-h-[70vh] overflow-y-auto p-5">
              {/* PERSONAL INFORMATION */}
              <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                  Personal Information
                </h3>

                {/* NAME */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="col-span-1">
                    {renderEditField("First Name", "firstName")}
                  </div>
                  <div className="col-span-1">
                    {renderEditField("Middle Name", "middleName")}
                  </div>
                  <div className="col-span-1">
                    {renderEditField("Last Name", "lastName")}
                  </div>
                </div>

                {/* STATE, ZONE AND MAHAJAN */}
                <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-5">
                  <div className="col-span-1">
                    {renderEditField("State", "state")}
                  </div>
                  <div className="col-span-1">
                    {renderEditField("District", "district")}
                  </div>
                  <div className="col-span-1">
                    {renderEditField("Zone", "zone")}
                  </div>
                  <div className="col-span-1">
                    {renderEditField("Mahajan", "mahajan")}
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="col-span-1 flex flex-col gap-5">
                    {renderEditField("Email", "email")}
                    {renderEditField("Mobile", "mobileNumber")}
                    {renderEditField("Date of Birth", "dateOfBirth", "date")}
                    {renderTextareaField("Address", "address", "2")}
                  </div>
                  <div className="col-span-1 flex flex-col gap-5">
                    {renderEditField("Lohana Connect ID", "lohanaConnectId")}
                    {renderFileField("Photograph", "photograph")}
                    {renderFileField("Signature", "applicantSignature")}
                  </div>
                </div>
              </div>

              {/* PROFESSIONAL DETAILS */}
              <div className="border border-greyBorder rounded-lg p-5 mb-5 bg-white shadow">
                <h3 className="text-md font-ibm-bold mb-4 text-red-800 border-b border-red-100 pb-2">
                  Professional Details
                </h3>
                <div className="p-0">
                  {/* TEACHING PROFESSIONALS */}
                  <div className="bg-white p-5 rounded-lg shadow-md border border-greyBorder">
                    <div className="grid grid-cols-2 gap-5">
                      {renderEditField("Institute", "institute")}
                      {renderEditField("Experience", "experienceYears")}
                    </div>
                    <div className="mt-5 grid grid-cols-3 gap-5">
                      {renderEditField("Qualification 1", "qualification1")}
                      {renderEditField("Qualification 2", "qualification2")}
                      {renderEditField("Qualification 3", "qualification3")}
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-5">
                      {renderTextareaField("Summary", "summary", "3")}
                      {renderTextareaField("Achievements", "achievements", "3")}
                    </div>
                    <div className="mt-5">
                      {renderFileField("Profile", "profile")}
                    </div>
                  </div>
                </div>
              </div>

              {/* DOCUMENTS */}
              <div className="border border-greyBorder rounded-lg p-5 bg-white shadow">
                <h3 className="text-md font-ibm-bold mb-4 text-logoRed border-b border-greyBorder pb-2">
                  Supporting Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {renderFileField("Proof of Identity", "proofOfIdentity")}

                  {renderFileField(
                    "Certified Transcripts and Achievement Certificates",
                    "certifiedTranscriptsAndAchievementCertificates"
                  )}
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="flex justify-end gap-2 p-5 border-t border-greyBorder">
              <Button
                onClick={() => setEditModal(false)}
                className="bg-greyBg text-black shadow-none hover:shadow-none font-ibm-semibold text-base"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-logoRed text-white shadow-none hover:shadow-none font-ibm-semibold text-base"
              >
                {formLoading ? "Updating..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default Teacher;
